import { DateTime } from 'luxon'

import { PagedResult } from '../DTOs/Common/PagedResult'
import { queryClient } from '../queryClient'

type SortValue = string | number | DateTime | null | undefined

export interface CachedSort<T> {
  /** The column the table is sorted by, named as the table names it. */
  field?: string
  direction?: 'asc' | 'desc'
  /** How to read each sortable column off a row. A column not listed falls back to the default order. */
  accessors: Record<string, (row: T) => SortValue>
  /** The order the server returns rows in when the table has asked for none. */
  fallback?: (row: T) => SortValue
  fallbackDirection?: 'asc' | 'desc'
}

function comparableValue(value: SortValue): string | number | null {
  if (value === null || value === undefined) return null
  if (DateTime.isDateTime(value)) return value.toMillis()
  // Note: an empty string is blank, not a value that sorts above every other - a row whose machine
  // name could not be resolved belongs at the bottom, not the top. A zero is a real number, though.
  if (typeof value === 'string' && value.trim() === '') return null
  return value
}

// Note: numeric collation, so machine names sort the way the crew reads them - PE002 before PE010,
// which a plain string comparison gets backwards. Blanks sort last either way round.
function compare(a: SortValue, b: SortValue): number {
  const left = comparableValue(a)
  const right = comparableValue(b)

  if (left === null) return right === null ? 0 : 1
  if (right === null) return -1
  if (typeof left === 'number' && typeof right === 'number') return left - right
  return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' })
}

// Note: offline the rows arrive in whatever order they happen to sit in the cache, which is not the
// order the server would have returned - the lists read as jumbled, and clicking a column header did
// nothing at all. Apply the table's sort here, and where it has asked for none, the order the server
// uses by default.
export function sortCachedRows<T>(rows: T[], sort?: CachedSort<T>): T[] {
  if (!sort) return rows

  const requested = sort.field ? sort.accessors[sort.field] : undefined
  const accessor = requested ?? sort.fallback
  if (!accessor) return rows

  const direction = requested ? sort.direction ?? 'asc' : sort.fallbackDirection ?? 'asc'
  const sign = direction === 'desc' ? -1 : 1

  return [...rows].sort((a, b) => sign * compare(accessor(a), accessor(b)))
}

// Note: offline, a filter cannot be applied by the server, and because the filter is part of the
// query key the filtered combination has no cache entry - the table dropped to "Not available
// offline" as soon as anything was typed or picked. The whole shift is already cached under the
// unfiltered key though, so filter that in memory instead. Pages call this from their fetchFn, so
// pagination, totals and rendering all behave exactly as they do online.
export function filterCachedRows<T>(
  unfilteredKeyPrefix: unknown[],
  predicate: (row: T) => boolean,
  sort?: CachedSort<T>,
): PagedResult<T> {
  // Note: matched on the prefix rather than a key built by hand. The full key carries the page size,
  // sort and filter segments useDataTable appends, and a hand-built one that disagreed about any of
  // them (a page size from useAutoPageSize, say) found nothing and the table read as empty. The page
  // with the most rows is the unfiltered one, which is what we want to filter in memory.
  let widest: PagedResult<T> | undefined
  for (const [, cached] of queryClient.getQueriesData<PagedResult<T>>({ queryKey: unfilteredKeyPrefix })) {
    if ((cached?.data?.length ?? 0) > (widest?.data?.length ?? 0)) widest = cached ?? undefined
  }

  const rows = sortCachedRows((widest?.data ?? []).filter(predicate), sort)
  return new PagedResult<T>(rows, rows.length, Math.max(1, rows.length), 1, Math.max(rows.length, 1))
}

export function matchesText(value: string | undefined | null, filter: string | undefined | null): boolean {
  if (!filter) return true
  return (value ?? '').toLowerCase().includes(filter.toLowerCase())
}
